import React, { useEffect, useState } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  Spinner,
  Center,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  chakra,
  useColorModeValue,
  Checkbox,
  Button,
  Flex,
  Input,
  useToast,
} from "@chakra-ui/react";
import { GetInventoryData } from "../../../InventoryServices/InventoryAPI/GetInventoryData";
import { AddRelatedProduct } from "../../../InventoryServices/InventoryRelatedDesigns/RelatedDesignsAPI";

const RelatedTabModal = ({ isOpen, onClose, parentInventoryId }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedIds, setSelectedIds] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const toast = useToast();

  const headerBg = useColorModeValue("blue.500", "blue.600");
  const headerColor = useColorModeValue("white", "gray.100");
  const rowHoverBg = useColorModeValue("blue.50", "gray.700");

  useEffect(() => {
    if (!isOpen) return;

    const fetchData = async () => {
      setLoading(true);
      try {
        const res = await GetInventoryData({ limit: 50, page: 1 });
        setItems(Array.isArray(res.data.data) ? res.data.data : []);
      } catch (err) {
        console.error("Error fetching inventory:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [isOpen]);

  const formatDateTime = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return `${date.getDate()}-${date.getMonth() + 1}-${date.getFullYear()}`;
  };

  const toggleSelection = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const filteredItems = items.filter(
    (item) =>
      item.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.productCode?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleSubmit = async () => {
    if (selectedIds.length === 0) {
      toast({
        title: "No product selected",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    try {
      setSubmitting(true);
      for (const relateId of selectedIds) {
        await AddRelatedProduct(parentInventoryId, {
          relate_inventory_id: relateId,
        });
      }

      toast({
        title: "Products related successfully",
        status: "success",
        duration: 2000,
        isClosable: true,
      });

      setSelectedIds([]); // Clear selection
      onClose();
    } catch (error) {
      console.error("Error relating products:", error);
      toast({
        title: "Failed to relate products",
        description: error?.response?.data?.message || "Something went wrong",
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setSubmitting(false);
    }
  };

  // Close button handler function with debugging
  const handleClose = (e) => {
    console.log("Close button clicked"); // Debug log
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    setSelectedIds([]); // Reset selection when closing
    setSearchTerm(""); // Reset search
    onClose();
  };

  // Debug function
  const debugClose = (source) => {
    console.log(`Close triggered from: ${source}`);
    handleClose();
  };

  return (
    <Modal 
      isOpen={isOpen} 
      onClose={() => debugClose('modal')} 
      size="6xl" 
      isCentered 
      scrollBehavior="inside"
      closeOnOverlayClick={true}
      closeOnEsc={true}
      blockScrollOnMount={false}
    >
      <ModalOverlay onClick={() => debugClose('overlay')} />
      <ModalContent borderRadius="xl">
        <ModalHeader 
          display="flex" 
          justifyContent="space-between" 
          alignItems="center"
        >
          <chakra.span fontWeight="bold">Select Related Design</chakra.span>
        </ModalHeader>
        
        {/* Close button with higher z-index and better positioning */}
        <ModalCloseButton 
          zIndex={9999}
          size="lg"
          color="gray.600"
          _hover={{ 
            bg: "red.100", 
            color: "red.600",
            transform: "scale(1.1)"
          }}
          onClick={(e) => {
            console.log("ModalCloseButton clicked");
            e.preventDefault();
            e.stopPropagation();
            debugClose('close-button');
          }}
        />
        
        <ModalBody pb={6}>
          {/* Search bar with proper spacing */}
          <Flex mb={4} justify="flex-end">
            <Input
              placeholder="Search..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              size="sm"
              maxW="250px"
              bg="white"
            />
          </Flex>

          <TableContainer
            border="1px solid"
            borderColor="gray.200"
            borderRadius="md"
            maxH="400px"
            overflowY="auto"
          >
            <Table size="sm" variant="striped">
              <Thead bg={headerBg} position="sticky" top={0} zIndex={1}>
                <Tr>
                  <Th color={headerColor}></Th>
                  <Th color={headerColor}>Product</Th>
                  <Th color={headerColor}>Code</Th>
                  <Th color={headerColor}>Category</Th>
                  <Th color={headerColor}>Created</Th>
                </Tr>
              </Thead>
              <Tbody>
                {loading ? (
                  <Tr>
                    <Td colSpan={5}>
                      <Center py={10}>
                        <Spinner size="lg" color="blue.500" />
                      </Center>
                    </Td>
                  </Tr>
                ) : filteredItems.length === 0 ? (
                  <Tr>
                    <Td colSpan={5} textAlign="center" py={6}>
                      No products found
                    </Td>
                  </Tr>
                ) : (
                  filteredItems.map((item) => {
                    const isChecked = selectedIds.includes(item.id);
                    return (
                      <Tr
                        key={item.id}
                        _hover={{ bg: rowHoverBg, cursor: "pointer" }}
                        onClick={() => toggleSelection(item.id)}
                      >
                        <Td onClick={(e) => e.stopPropagation()}>
                          <Checkbox
                            isChecked={isChecked}
                            onChange={() => toggleSelection(item.id)}
                          />
                        </Td>
                        <Td fontWeight="600">{item.title}</Td>
                        <Td>{item.productCode}</Td>
                        <Td>{item.category}</Td>
                        <Td>
                          <chakra.span fontSize="sm" color="gray.600">
                            {formatDateTime(item.created_at)}
                          </chakra.span>
                        </Td>
                      </Tr>
                    );
                  })
                )}
              </Tbody>
            </Table>
          </TableContainer>

          <Flex justify="flex-end" mt={4}>
            <Button
              colorScheme="blue"
              onClick={handleSubmit}
              isDisabled={selectedIds.length === 0 || submitting}
              isLoading={submitting}
              loadingText="Relating..."
            >
              Add Selected ({selectedIds.length})
            </Button>
          </Flex>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default RelatedTabModal;